import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {useParams} from 'react-router-dom';
import {
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {FlatSelectDropdown} from '../../../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {Block, CheckCircle, RemoveCircle} from '@mui/icons-material';
import {SvgIconComponent} from '@mui/icons-material';
import GenericHeader from '../../../../../../../components/generic-header/generic-header';
import {RolePermissionType} from '../../../../../../../backend/enums/role-permission-type';
import {AttendanceTrackerAssignment} from '../../../../../../../backend/responses/attendance-tracker-assignment';
import {UserType} from '../../../../../../../backend/enums/user-type';
import {useAuth} from '../../../../../../../contexts/auth/context';
import {useSnackBar} from '../../../../../../../contexts/snack-bar/context';
import {ScreenPaths} from '../../../../../../../enums';
import {AttendanceTrackerAssignmentType} from '../../../../../../../backend/enums/attendance-tracker-assignment-type';
import {useFetchAllItems} from '../../../../../../../hooks/fetch-all-items';
import {useUsersInfoCache} from '../../../../../../../hooks/use-users-info-cache';
import {useFetchEntityUsers} from '../../../../../../../hooks/use-fetch-entity-users';
import {Pallete} from '../../../../../../../theme/bluebird';
import {EntityAttendanceTrackerAssignmentsRepository} from '../../../../../../../repositories/entity-attendance-tracker-assignments';
import {RolesRepository} from '../../../../../../../repositories/roles';
import {translations} from '../../../../../../../i18n/translations';
import './attendance-tracker-assignments.professional-caregiver.screen.scss';

const ASSIGNMENT_TYPES: Array<{
  type: AttendanceTrackerAssignmentType;
  translation: string;
  Icon: SvgIconComponent;
  color: string;
}> = [
  {
    type: AttendanceTrackerAssignmentType.Mandatory,
    translation: translations.attendance_tracker_assignments_screen.assignment_types.mandatory,
    Icon: CheckCircle,
    color: Pallete.green.base,
  },
  {
    type: AttendanceTrackerAssignmentType.Optional,
    translation: translations.attendance_tracker_assignments_screen.assignment_types.optional,
    Icon: RemoveCircle,
    color: Pallete.yellow.base,
  },
  {
    type: AttendanceTrackerAssignmentType.NotApplicable,
    translation: translations.attendance_tracker_assignments_screen.assignment_types.not_applicable,
    Icon: Block,
    color: Pallete.gray.grayDisable,
  },
];

export default function AttendanceTrackerAssignmentsProfessionalCaregiverOfficeScreen() {
  const {t} = useTranslation();
  const {entityId}: {entityId: string} = useParams();
  const {hasReadPermission, hasWritePermission} = useAuth();
  const canWrite = hasWritePermission(RolePermissionType.Team_AttendanceTracker_Assignments);
  const snackBarContext = useSnackBar();

  const {items: professionals, isLoading} = useFetchEntityUsers({
    userType: UserType.Caregiver,
    activeUsers: true,
  });

  // The roles of each professional are only shown to users that can see the roles of the entity
  const canReadRoles =
    hasReadPermission(RolePermissionType.Team_Permissions) ||
    hasReadPermission(RolePermissionType.Team_Professionals);
  const {items: roles} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => RolesRepository.instance.getRoles(entityId, limit, skip),
    enabled: canReadRoles,
    deps: [entityId, canReadRoles],
  });
  const roleNamesById = useMemo(
    () => new Map((roles ?? []).map((role) => [role.id, role.name])),
    [roles]
  );

  // Assignments that were already set. Professionals without one are not applicable.
  const {
    items: storedAssignments,
    isLoading: isLoadingAssignments,
    error: assignmentsError,
  } = useFetchAllItems({
    fetchFunction: ({limit, skip}) =>
      EntityAttendanceTrackerAssignmentsRepository.instance.getAssignments(entityId, limit, skip),
    deps: [entityId],
  });

  // Assignments changed on this screen, by user id. They take precedence over the stored ones.
  const [changedAssignments, setChangedAssignments] = useState<
    Record<string, AttendanceTrackerAssignmentType>
  >({});
  // Last change of each assignment changed on this screen, as answered by the backend
  const [updatedAssignments, setUpdatedAssignments] = useState<
    Record<string, AttendanceTrackerAssignment>
  >({});
  // Users with a change being saved
  const [savingUserIds, setSavingUserIds] = useState<ReadonlySet<string>>(new Set());

  const getAssignment = useCallback(
    (userId: string): AttendanceTrackerAssignmentType =>
      changedAssignments[userId] ??
      storedAssignments?.find((stored) => stored.userId === userId)?.assignment ??
      AttendanceTrackerAssignmentType.NotApplicable,
    [changedAssignments, storedAssignments]
  );

  const getLastChange = (userId: string): AttendanceTrackerAssignment | undefined =>
    updatedAssignments[userId] ?? storedAssignments?.find((stored) => stored.userId === userId);

  // Authors of the last changes, to show their names
  const lastChangeAuthorsIds = useMemo(
    () =>
      [...(storedAssignments ?? []), ...Object.values(updatedAssignments)].map(
        (assignment) => assignment.updatedByUserId
      ),
    [storedAssignments, updatedAssignments]
  );
  const {users: authors} = useUsersInfoCache(lastChangeAuthorsIds);

  useEffect(() => {
    if (assignmentsError) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.attendance_tracker_assignments_screen.load_error_toast),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentsError]);

  const updateAssignment = async (userId: string, assignment: AttendanceTrackerAssignmentType) => {
    const previousAssignment = getAssignment(userId);
    if (assignment === previousAssignment) {
      return;
    }

    // Optimistic update
    setChangedAssignments((previous) => ({...previous, [userId]: assignment}));
    setSavingUserIds((previous) => new Set(previous).add(userId));

    try {
      const updated = await EntityAttendanceTrackerAssignmentsRepository.instance.updateAssignment(
        entityId,
        userId,
        {assignment}
      );
      setChangedAssignments((previous) => ({...previous, [userId]: updated.assignment}));
      setUpdatedAssignments((previous) => ({...previous, [userId]: updated}));
    } catch (e) {
      console.error(e);

      // Revert
      setChangedAssignments((previous) => ({...previous, [userId]: previousAssignment}));
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.attendance_tracker_assignments_screen.update_error_toast),
      });
    } finally {
      setSavingUserIds((previous) => {
        const next = new Set(previous);
        next.delete(userId);
        return next;
      });
    }
  };

  const assignmentOptions = ASSIGNMENT_TYPES.map(({type, translation}) => ({
    key: type,
    label: t(translation),
  }));

  const teamUrl = `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`;

  return (
    <Box className="attendance-tracker-assignments">
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.screen_paths.professional_caregiver_team),
            link: teamUrl,
          },
          {
            label: t(translations.teamSideMenu.attendanceTracker.title),
            link: `${teamUrl}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments}`,
          },
          {
            label: t(translations.teamSideMenu.attendanceTracker.assignments),
            link: `${teamUrl}${ScreenPaths.ProfessionalCaregiver_AttendanceTracker}${ScreenPaths.ProfessionalCaregiver_AttendanceTrackerAssignments}`,
          },
        ]}
        isLoading={false}
        leftColumn={
          <Typography className="attendance-tracker-assignments-header-title" noWrap>
            {canWrite
              ? t(translations.attendance_tracker_assignments_screen.subtitle.write)
              : t(translations.attendance_tracker_assignments_screen.subtitle.read)}
          </Typography>
        }
      />
      <Card className="attendance-tracker-assignments-card" elevation={0}>
        {isLoading || isLoadingAssignments ? (
          <Box className="attendance-tracker-assignments-empty">
            <CircularProgress />
          </Box>
        ) : !professionals || professionals.length === 0 ? (
          <Box className="attendance-tracker-assignments-empty">
            <Typography>{t(translations.attendance_tracker_assignments_screen.empty)}</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className="attendance-tracker-assignments-header-cell">
                  {t(translations.attendance_tracker_assignments_screen.columns.professional)}
                </TableCell>
                <TableCell className="attendance-tracker-assignments-header-cell">
                  {t(translations.attendance_tracker_assignments_screen.columns.assignment)}
                </TableCell>
                <TableCell className="attendance-tracker-assignments-header-cell">
                  {t(translations.attendance_tracker_assignments_screen.columns.last_change)}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {professionals.map(({user, affiliation}) => {
                const userId = user.id ?? '';
                const roleNames = (canReadRoles ? (affiliation.rolesIds ?? []) : [])
                  .map((roleId) => roleNamesById.get(roleId))
                  .filter((name): name is string => Boolean(name));
                const selectedAssignmentType =
                  ASSIGNMENT_TYPES.find(({type}) => type === getAssignment(userId)) ??
                  ASSIGNMENT_TYPES[ASSIGNMENT_TYPES.length - 1];
                const AssignmentIcon = selectedAssignmentType.Icon;
                const lastChange = getLastChange(userId);
                const lastChangeAuthor = lastChange?.updatedByUserId
                  ? authors[lastChange.updatedByUserId]
                  : undefined;
                const lastChangeDate = lastChange
                  ? moment(lastChange.updatedAt).format('DD/MM/YY HH:mm')
                  : '';
                return (
                  <TableRow key={userId}>
                    <TableCell>
                      <Box className="attendance-tracker-assignments-professional">
                        <Avatar src={user.picture} alt={user.name} />
                        <Box className="attendance-tracker-assignments-professional-info">
                          <Typography>{user.name}</Typography>
                          {roleNames.length > 0 && (
                            <Box className="attendance-tracker-assignments-roles">
                              {roleNames.map((roleName) => (
                                <Chip key={roleName} size="small" label={roleName} />
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box className="attendance-tracker-assignments-assignment">
                        <Box className="attendance-tracker-assignments-assignment-select">
                          <FlatSelectDropdown
                            options={assignmentOptions}
                            value={assignmentOptions.find(
                              ({key}) => key === selectedAssignmentType.type
                            )}
                            disabled={!canWrite || savingUserIds.has(userId)}
                            onChange={(option) =>
                              updateAssignment(
                                userId,
                                option.key as AttendanceTrackerAssignmentType
                              )
                            }
                          />
                        </Box>
                        <AssignmentIcon sx={{color: selectedAssignmentType.color}} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      {lastChange ? (
                        <Box className="attendance-tracker-assignments-last-change">
                          <Typography>
                            {t(
                              lastChangeAuthor
                                ? translations.attendance_tracker_assignments_screen
                                    .last_change_summary
                                : translations.attendance_tracker_assignments_screen
                                    .last_change_summary_no_author,
                              {
                                name: lastChangeAuthor?.name,
                                date: lastChangeDate,
                              }
                            )}
                          </Typography>
                          <Typography>{moment(lastChange.updatedAt).fromNow()}</Typography>
                        </Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </Box>
  );
}
